import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth } from '../utils/authUtils';

const Header = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [openFeedbacksCount, setOpenFeedbacksCount] = useState(0);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchOpenFeedbacksCount();
    // Rafraîchir le compteur toutes les 30 secondes
    const interval = setInterval(fetchOpenFeedbacksCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fermer le dropdown si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMoreMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchOpenFeedbacksCount = async () => {
    try {
      const response = await fetchWithAuth('/api/feedbacks');
      if (response.ok) {
        const feedbacks = await response.json();
        const openCount = feedbacks.filter(f => f.status === 'open').length;
        setOpenFeedbacksCount(openCount);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du nombre de feedbacks:', error);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      logout();
    }
  };

  // Vérifier si on est dans une route "more menu"
  const isMoreMenuActive = ['/version-dashboard', '/statistics', '/infrastructure-overview', '/feedback', '/update-planning', '/deployable-versions'].includes(location.pathname);
  
  return (
    <header className="navbar shadow-md">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link to="/" className="text-2xl font-bold flex items-center">
          <svg className="w-8 h-8 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
          </svg>
          Parc Info
        </Link>
        <nav>
          <ul className="flex space-x-6 items-center">
            {/* Clients - Écran principal */}
            <li>
              <Link 
                to="/" 
                className={`navbar-link font-semibold ${location.pathname === '/' ? 'active' : ''}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Clients
              </Link>
            </li>

            {/* Suivi des MAJ - Très important */}
            <li>
              <Link 
                to="/update-tracking" 
                className={`navbar-link ${location.pathname === '/update-tracking' ? 'active' : ''} flex items-center`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h2m9-2H7m5-10V3a1 1 0 00-1-1H9a1 1 0 00-1 1v2m2 0h4v2H8V5zm0 10h8m-8 0v6a2 2 0 002 2h6a2 2 0 002-2v-6m-8 0v-2h8v2" />
                </svg>
                Suivi des MAJ
              </Link>
            </li>

            {/* Vue des Versions - Élément principal */}
            <li>
              <Link 
                to="/versions" 
                className={`navbar-link ${location.pathname === '/versions' ? 'active' : ''}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Vue des Versions
              </Link>
            </li>

            {/* Dossiers Ingénierie */}
            <li>
              <Link 
                to="/engineering" 
                className={`navbar-link ${location.pathname.startsWith('/engineering') || location.pathname.includes('/engineering') ? 'active' : ''} flex items-center`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 1v6m8-6v6" />
                </svg>
                Dossiers Ingé
              </Link>
            </li>

            {/* Menu "Plus" pour les options secondaires */}
            <li className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className={`navbar-link flex items-center ${isMoreMenuActive || isMoreMenuOpen ? 'active' : ''}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
                Plus
                <svg className={`w-4 h-4 ml-1 transform transition-transform ${isMoreMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown menu */}
              {isMoreMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    {/* Planification - En développement */}
                    <Link 
                      to="/update-planning" 
                      className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                        location.pathname === '/update-planning' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      onClick={() => setIsMoreMenuOpen(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Planification
                      <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
                        Work in progress
                      </span>
                    </Link>
                    
                    {/* Versions Déployables */}
                    <Link
                      to="/deployable-versions"
                      className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                        location.pathname === '/deployable-versions' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      onClick={() => setIsMoreMenuOpen(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Versions Deployables
                      <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                        Nouveau
                      </span>
                    </Link>

                    <div className="border-t border-gray-200 my-1"></div>

                    {/* Tableau de bord versions */}
                    <Link
                      to="/version-dashboard" 
                      className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                        location.pathname === '/version-dashboard' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      onClick={() => setIsMoreMenuOpen(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Tableau de bord versions
                      <span className="ml-2 flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                      </span>
                    </Link>
                    
                    {/* Vue d'ensemble Infrastructure - Principale */}
                    <Link 
                      to="/infrastructure-overview" 
                      className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                        location.pathname === '/infrastructure-overview' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      onClick={() => setIsMoreMenuOpen(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Vue d'ensemble Infrastructure
                      <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                        Complet
                      </span>
                    </Link>
                    
                    <div className="border-t border-gray-200 my-1"></div>
                    
                    {/* Statistiques */}
                    <Link 
                      to="/statistics" 
                      className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                        location.pathname === '/statistics' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      onClick={() => setIsMoreMenuOpen(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Statistiques
                    </Link>
                    
                    {/* Signalements */}
                    <Link 
                      to="/feedback" 
                      className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                        location.pathname === '/feedback' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      onClick={() => setIsMoreMenuOpen(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                      Signalements
                      {openFeedbacksCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {openFeedbacksCount}
                        </span>
                      )}
                    </Link>
                  </div>
                </div>
              )}
            </li>
          </ul>
        </nav>
        
        {/* Section utilisateur */}
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-600">
            Connecté en tant que <span className="font-medium">{user?.username}</span>
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-gray-100 transition-colors"
            title="Se déconnecter"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
